import { computed, type ComputedRef } from 'vue';
import { useAuth } from '#imports';
import type {
  ImpersonatedUserInfo,
  StartImpersonationError,
} from '~~/shared/types/impersonation';

interface StartInput {
  targetUserId: string;
  reason?: string;
  acknowledgeParallel?: boolean;
}

/**
 * Composable for admin impersonation (010-admin-impersonation).
 * Reactively exposes session impersonation state and start/exit actions.
 */
export function useImpersonation() {
  const { data: session, getSession, getCsrfToken } = useAuth();

  const isImpersonating: ComputedRef<boolean> = computed(
    () => !!session.value?.user?.impersonationSessionId
  );

  const impersonatedUser: ComputedRef<ImpersonatedUserInfo | null> = computed(() => {
    const u = session.value?.user;
    if (!u?.impersonatedUserId || !u.impersonatedUserEmail) return null;
    return { id: u.impersonatedUserId, email: u.impersonatedUserEmail };
  });

  const originalAdminId: ComputedRef<string | null> = computed(
    () => session.value?.user?.originalAdminId ?? null
  );

  const sessionId: ComputedRef<string | null> = computed(
    () => session.value?.user?.impersonationSessionId ?? null
  );

  async function start(input: StartInput): Promise<{ sessionId: string }> {
    try {
      const csrfToken = await getCsrfToken();
      const res = await $fetch<{ sessionId: string; targetUser: { id: string; email: string; name: string | null }; startedAt: string }>(
        '/api/admin/impersonation/start',
        {
          method: 'POST',
          body: input,
          headers: { 'X-CSRF-Token': csrfToken },
        }
      );
      await getSession({ force: true });
      return { sessionId: res.sessionId };
    } catch (err: any) {
      if (err?.data && typeof err.data === 'object' && 'code' in err.data) {
        throw err.data as StartImpersonationError;
      }
      throw err;
    }
  }

  async function exit(): Promise<{ redirectTo: string }> {
    const csrfToken = await getCsrfToken();
    const res = await $fetch<{ endedAt: string; redirectTo: string }>(
      '/api/admin/impersonation/exit',
      {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
      }
    );
    await getSession({ force: true });
    return { redirectTo: res.redirectTo };
  }

  return {
    isImpersonating,
    impersonatedUser,
    originalAdminId,
    sessionId,
    start,
    exit,
  };
}
